import { API_BASE, apiGet, apiPost, apiPut } from './api';

/**
 * THE client's one door onto `/api/timesheets` (stage S3b, owner rulings
 * 2026-10-24). Every timesheet request the screens make goes through here, so
 * a change to a route shape (S3a was under review while this was written) is
 * one edit in one file. The server routes are `server/routes/timesheets.js`;
 * the response shapes are its `managerPerson` / `managerUnits` serialisers and
 * `server/helpers/timesheetCrewView.js` (the crew allowlist — no money).
 *
 * 🔑 Each call resolves `{ ok, status, body }` and never throws on an HTTP
 * refusal: the screens show the server's own `error` text (a refusal names
 * what was wrong — `week_submitted`, an unknown field, a foreign id). A
 * network failure still rejects, and the caller's `catch` shows it.
 *
 * 🔒 Nothing here decides authority. The page picks the manager or crew
 * screen from `capabilities.canManageBudget`; the server refuses
 * independently on every route.
 */

const BASE = `${API_BASE}/api/timesheets`;

async function settle(resPromise) {
  const res = await resPromise;
  let body = null;
  try { body = await res.json(); } catch (_) { body = null; }
  return { ok: res.ok, status: res.status, body };
}

const enc = encodeURIComponent;

// ── manager (canManageBudget in the active department) ─────────────────────

/** GET /week — the manager week view. `unitId` narrows (null = all units). */
export function fetchManagerWeek(weekStart, unitId = null) {
  const qs = new URLSearchParams({ week_start: weekStart });
  if (unitId != null) qs.set('unit', String(unitId));
  return settle(apiGet(`${BASE}/week?${qs.toString()}`));
}

/**
 * PUT /unit-days — the generic unit timecard; the response carries `conflicts` and `affected`.
 * `editReason` (2026-10-29): REQUIRED by the server when the save changes a SUBMITTED week
 * (400 `edit_reason_required`); sent only when given.
 */
export function saveUnitDays({ unitIds, days, editReason }) {
  const body = { sub_department_ids: unitIds, days };
  if (editReason) body.edit_reason = editReason;
  return settle(apiPut(`${BASE}/unit-days`, body));
}

/**
 * POST /edit-impact — EDIT AFTER SUBMIT (2026-10-29): BEFORE a manager save, which SUBMITTED weeks
 * it would change and each pay before → after (read-only; the reason dialog's list).
 * `change` is `{ kind: 'person'|'unit'|'away'|'away_end', ...the save's own body }`.
 */
export function previewEditImpact(change) {
  return settle(apiPost(`${BASE}/edit-impact`, change));
}

/** GET /people/:lineageId/history — one person-week's activity history, newest first (manager). */
export function fetchPersonWeekHistory(lineageId, weekStart) {
  return settle(apiGet(`${BASE}/people/${enc(lineageId)}/history?week_start=${enc(weekStart)}`));
}

/** GET /units/:unitId/history — one unit's mass edits over `from`..`to`, newest first (manager). */
export function fetchUnitHistory(unitId, from, to) {
  return settle(apiGet(`${BASE}/units/${enc(unitId)}/history?from=${enc(from)}&to=${enc(to)}`));
}

/**
 * POST /payroll-runs — EXPORT TRACKING (D2): call when the export is DOWNLOADED or PRINTED, never
 * on a preview. Same filters as `fetchTimesheetExport`; the server re-derives the weeks.
 */
export function recordPayrollRun({ from, to, unitIds = null, lineageIds = null, includeProjected = false, format, generatedAt }) {
  // `generatedAt` = the GET's own `generated_at` (review m-1): a week changed after it stays flagged.
  const body = { from, to, format, include_projected: !!includeProjected, generated_at: generatedAt };
  if (unitIds && unitIds.length) body.units = unitIds.join(',');
  if (lineageIds && lineageIds.length) body.people = lineageIds.join(',');
  return settle(apiPost(`${BASE}/payroll-runs`, body));
}


/** POST /calc-preview — unsaved edits through the server's calculator (hours only, writes nothing). */
export function previewCalc(days) {
  return settle(apiPost(`${BASE}/calc-preview`, { days }));
}

/** PUT /people/:lineageId/days/:date — a manager's write to one person's day. */
export function saveManagerPersonDay(lineageId, date, body) {
  return settle(apiPut(`${BASE}/people/${enc(lineageId)}/days/${enc(date)}`, body));
}

/**
 * POST /conflicts/resolve — `items: [{ lineage_id, work_date, field, choice }]`; `editReason` when
 * the settle reaches a SUBMITTED week (RC-3, asked by `useEditReason`).
 */
export function resolveConflictItems(items, editReason) {
  return settle(apiPost(`${BASE}/conflicts/resolve`, editReason ? { items, edit_reason: editReason } : { items }));
}

/** POST /conflicts/resolve — the bulk form ("Use unit value for all" / "Keep everyone's own"). */
export function resolveAllConflicts({ weekStart, choice, unitId = null }) {
  const body = { week_start: weekStart, choice, all: true };
  if (unitId != null) body.sub_department_id = unitId;
  return settle(apiPost(`${BASE}/conflicts/resolve`, body));
}

/** POST /weeks/submit — one person-week (Reconciled only). */
export function submitPersonWeek(lineageId, weekStart) {
  return settle(apiPost(`${BASE}/weeks/submit`, { lineage_id: lineageId, week_start: weekStart }));
}

/**
 * POST /weeks/lock — the unit timecard's "Submit week": the people whose HOME
 * sub-department is in `unitIds` (null = whole department); ready weeks are
 * submitted, the rest locked for crew and left to the buyer.
 */
export function lockDepartmentWeek(weekStart, unitIds = null) {
  const body = { week_start: weekStart };
  if (unitIds != null) body.sub_department_ids = unitIds;
  return settle(apiPost(`${BASE}/weeks/lock`, body));
}

/** POST /weeks/commit-all — "Commit all reconciled timesheets"; reports `skipped`. */
export function commitAllReconciled(weekStart, unitId = null) {
  const body = { week_start: weekStart };
  if (unitId != null) body.sub_department_id = unitId;
  return settle(apiPost(`${BASE}/weeks/commit-all`, body));
}

/** POST /weeks/reopen — the reason is REQUIRED (the server refuses a blank one too). */
export function reopenPersonWeek(lineageId, weekStart, reason) {
  return settle(apiPost(`${BASE}/weeks/reopen`, { lineage_id: lineageId, week_start: weekStart, reason }));
}

/** POST /away — `toDate` null = "until further notice"; `editReason` as for every manager save. */
export function createAwayPeriod({ lineageId, fromDate, toDate, reason, note, editReason }) {
  const body = { lineage_id: lineageId, from_date: fromDate, to_date: toDate, reason };
  if (note) body.note = note;
  if (editReason) body.edit_reason = editReason;
  return settle(apiPost(`${BASE}/away`, body));
}

/**
 * GET /export — whole weeks `from`..`to` (Mondays), everyone or chosen HOME
 * sub-departments (`unitIds`) or chosen people (`lineageIds`). Open weeks carry
 * money only with `includeProjected` (owner ruling C); the server prices nothing
 * differently from the screen (`server/helpers/timesheetExport.js`).
 */
export function fetchTimesheetExport({ from, to, unitIds = null, lineageIds = null, includeProjected = false }) {
  const qs = new URLSearchParams({ from, to });
  if (unitIds && unitIds.length) qs.set('units', unitIds.join(','));
  if (lineageIds && lineageIds.length) qs.set('people', lineageIds.join(','));
  if (includeProjected) qs.set('include_projected', '1');
  return settle(apiGet(`${BASE}/export?${qs.toString()}`));
}

/** POST /away/:awayId/end — closes an open Away period on `toDate`. */
export function endAwayPeriod(awayId, toDate, editReason) {
  const body = { to_date: toDate };
  if (editReason) body.edit_reason = editReason;
  return settle(apiPost(`${BASE}/away/${enc(awayId)}/end`, body));
}

// ── crew (the caller's own lineage only; never money) ──────────────────────

/** GET /my-week — the caller's own days and statuses. */
export function fetchMyWeek(weekStart) {
  return settle(apiGet(`${BASE}/my-week?week_start=${enc(weekStart)}`));
}

/** PUT /my-days/:lineageId/:date — `{ fields?, gag_id?, notes? }`; the unit is `fields.sub_department_id`. */
export function saveMyDay(lineageId, date, body) {
  return settle(apiPut(`${BASE}/my-days/${enc(lineageId)}/${enc(date)}`, body));
}

/**
 * GET /my-export — the crew member's OWN whole weeks `from`..`to` (Mondays, at most 13), each the
 * /my-week allowlist: hours only, never money (owner 2026-11-01). Records no payroll run.
 */
export function fetchMyExport({ from, to }) {
  return settle(apiGet(`${BASE}/my-export?${new URLSearchParams({ from, to }).toString()}`));
}

/** POST /my-weeks/:lineageId/send — the crew member's "Submit week" (a hand-in; locks it for them). */
export function sendMyWeek(lineageId, weekStart) {
  return settle(apiPost(`${BASE}/my-weeks/${enc(lineageId)}/send`, { week_start: weekStart }));
}

// ── the department's pick lists (not timesheet routes) ─────────────────────

/** The units (sub-departments) of a department, for the unit pickers. */
export function fetchUnits(departmentId) {
  return settle(apiGet(`${API_BASE}/api/sub-departments?department_id=${enc(departmentId)}`));
}

/** The department's live gags, for the Job picker (`{ gags: [...] }`). */
export function fetchGags() {
  return settle(apiGet(`${API_BASE}/api/gags`));
}
